import { useLayoutEffect, useRef } from "react";
import { useLensApp } from "../useLensApp.jsx";
import { userFacingError } from "../user-facing-error.js";

function statusDotClass(status) {
  if (!status) return "dot";
  if (status.startsWith("error") || status.includes("failed")) return "dot error";
  if (status === "running" || status === "ready" || status.includes("loaded") || status.includes("attached")) {
    return "dot ok";
  }
  return "dot";
}

function formatSourceText(source) {
  if (!source) return "none";
  const size = source.width && source.height ? `${source.width}x${source.height}` : "";
  return [source.label || source.type || "camera", size].filter(Boolean).join(" ");
}

export default function Stage() {
  const {
    status, source, frameCount, output, activeLenses, coreVersion, renderFps, renderFpsSource,
    settings, cameraMode, cameraTransition, previewShellRef, previewUrl, initialized, lastError, runtimeStatus, actions,
  } = useLensApp();
  const previewWrapRef = useRef(null);

  useLayoutEffect(() => {
    const wrap = previewWrapRef.current;
    const shell = previewShellRef.current;
    if (!wrap || !shell) return undefined;

    let frame = 0;
    const resize = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = 0;
        const styles = getComputedStyle(wrap);
        const paddingX = Number.parseFloat(styles.paddingLeft) + Number.parseFloat(styles.paddingRight);
        const paddingY = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom);
        const availableWidth = Math.max(1, wrap.clientWidth - paddingX);
        const availableHeight = Math.max(1, wrap.clientHeight - paddingY);
        const aspect = Math.max(0.001, output.width / Math.max(1, output.height));
        let width = availableWidth;
        let height = width / aspect;
        if (height > availableHeight) {
          height = availableHeight;
          width = height * aspect;
        }
        shell.style.width = `${Math.floor(width)}px`;
        shell.style.height = `${Math.floor(height)}px`;
        shell.style.setProperty("--preview-aspect", `${Math.max(1, output.width)} / ${Math.max(1, output.height)}`);
      });
    };

    resize();
    const observer = window.ResizeObserver ? new ResizeObserver(resize) : null;
    observer?.observe(wrap);
    window.addEventListener("resize", resize);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [output.width, output.height, previewShellRef]);

  const apiBase = settings?.apiBaseUrl
    ? `API ${settings.apiBaseUrl}`
    : (settings?.apiLastError ? `API error: ${settings.apiLastError}` : "API off");
  const nativeInput = runtimeStatus?.status?.inputCamera;
  const selectedCameraLabel = source?.label
    || (cameraMode === "virtual-camera" ? nativeInput?.label : "")
    || settings?.cameraLabel
    || "";
  const cameraPillText = cameraMode === "off"
    ? "camera released"
    : (selectedCameraLabel || "camera starting");
  const sourceText = selectedCameraLabel
    ? `input ${source ? formatSourceText(source) : selectedCameraLabel} -> output ${output.width}x${output.height}${output.mode === "match-input" ? " match" : ""}`
    : `output ${output.width}x${output.height}${output.mode === "match-input" ? " match input" : " fixed"}`;
  const renderFpsText = renderFps > 0 ? renderFps.toFixed(1) : "--";
  const previewVisible = cameraMode === "preview" && !cameraTransition;
  const displayedMode = cameraTransition || cameraMode;
  const modeTitle = displayedMode === "virtual-camera"
    ? (cameraTransition ? "Sending to Virtual Camera…" : "Virtual Camera is active")
    : (displayedMode === "preview" ? "Starting camera preview…" : "Camera is off");
  const modeDetail = displayedMode === "virtual-camera"
    ? "The native renderer owns the camera and sends the rendered lens output to other apps."
    : (displayedMode === "preview"
      ? "The physical camera is moving back into this preview."
      : "LensCore is not holding the physical camera. Choose Preview or Virtual to resume.");
  const visibleError = userFacingError(lastError);

  return (
    <main className={`stage${visibleError ? " has-error" : ""}`}>
      <header className="topbar">
        <div className="brand">
          <div className="mark" aria-hidden="true"></div>
          <div>
            <h1>Lens Host</h1>
            <div className="subtle">{apiBase}</div>
          </div>
        </div>
        <div className="status-row">
          <div className="pill"><span className={statusDotClass(status)}></span><span>{status}</span></div>
          <div className="pill">{cameraPillText}</div>
        </div>
      </header>

      {visibleError && (
        <div className="stage-error-banner" role="alert">
          <div>
            <strong>{visibleError.title}</strong>
            <span>{visibleError.message}</span>
          </div>
          <button type="button" onClick={() => actions.openUtilityPanel("diagnostics").catch(actions.setError)}>
            Diagnostics
          </button>
        </div>
      )}

      <div className="preview-wrap" ref={previewWrapRef}>
        <div className="preview-shell" ref={previewShellRef}>
          <iframe
            ref={actions.attachPreviewFrame}
            src={previewUrl}
            title="LensCore cloud renderer preview"
            allow="camera; microphone; autoplay"
            id="previewCanvas"
            className={previewVisible ? "" : "preview-inactive"}
          />
          {!previewVisible && (
            <div className="camera-mode-placeholder">
              <div className={`camera-mode-icon ${displayedMode}`} aria-hidden="true" />
              <strong>{modeTitle}</strong>
              <span>{modeDetail}</span>
            </div>
          )}
          <div className={`empty-state${initialized || !previewVisible ? " hidden" : ""}`}>Connecting to cloud renderer…</div>
        </div>
      </div>

      <div className="meters">
        <div className="meter">
          <div className="label">Frames</div>
          <div className="value">{frameCount}</div>
        </div>
        <div className="meter">
          <div className="label">Source</div>
          <div className="value">{sourceText}</div>
        </div>
        <div className="meter">
          <div className="label">Lens Stack</div>
          <div className="value">{activeLenses.length}</div>
        </div>
        <div className="meter">
          <div className="label">{renderFpsSource === "native-cef" ? "CEF Output FPS" : "Render FPS"}</div>
          <div className="value">{`${renderFpsText} / ${output.fps}`}</div>
        </div>
        <div className="meter">
          <div className="label">Runtime</div>
          <div className="value">{coreVersion || "--"}</div>
        </div>
      </div>
    </main>
  );
}
